/**
 * Shell-mounted presence channel. Mount exactly once (in RealtimeProvider, alongside the
 * single hub connection) — it owns the single set of presence transport wiring:
 *
 * - relays `PresenceMemberActive` / `PresenceMemberLeft` into the roster store (parsed at the
 *   boundary; malformed payloads are dropped),
 * - answers `PresenceSyncRequested` by re-announcing held refs (via the participation manager),
 * - drives the heartbeat and TTL-prune timers,
 * - re-points the participation manager at the connection and re-joins on (re)connect, clearing
 *   the stale roster so it is rebuilt fresh from the new socket.
 *
 * Per-record join/leave is acquired by `useRecordPresence` / `useObservedRecordPresence`; this
 * hook never decides which records to join, only how the connection is serviced.
 */

import { useEffect } from "react";

import { useRealtimeConnection, useRealtimeStateStore } from "../useConnectionState";
import { presenceParticipation } from "./presenceParticipation";
import { presenceStore } from "./presenceStore";
import {
    PRESENCE_ENABLED,
    PRESENCE_EVENTS,
    PRESENCE_HEARTBEAT_MS,
    PRESENCE_PRUNE_INTERVAL_MS,
    PRESENCE_TTL_MS,
    presenceMemberActiveSchema,
    presenceMemberLeftSchema,
    presenceSyncRequestedSchema,
} from "./presenceWire";

export function usePresenceChannel(): void {
    const connection = useRealtimeConnection();
    const stateStore = useRealtimeStateStore();

    useEffect(() => {
        if (!PRESENCE_ENABLED || connection === null) {
            return;
        }

        const handleActive = (raw: unknown): void => {
            const parsed = presenceMemberActiveSchema.safeParse(raw);
            if (!parsed.success) {
                console.warn("PresenceMemberActive payload rejected at boundary", parsed.error);
                return;
            }
            const payload = parsed.data;
            presenceStore.upsertActive(
                payload.entityType,
                payload.entityId,
                payload.member,
                Date.now(),
            );
        };

        const handleLeft = (raw: unknown): void => {
            const parsed = presenceMemberLeftSchema.safeParse(raw);
            if (!parsed.success) {
                console.warn("PresenceMemberLeft payload rejected at boundary", parsed.error);
                return;
            }
            const payload = parsed.data;
            presenceStore.remove(payload.entityType, payload.entityId, payload.connectionId);
        };

        const handleSyncRequested = (raw: unknown): void => {
            const parsed = presenceSyncRequestedSchema.safeParse(raw);
            if (!parsed.success) {
                console.warn("PresenceSyncRequested payload rejected at boundary", parsed.error);
                return;
            }
            const payload = parsed.data;
            presenceParticipation.replyToSync({
                entityType: payload.entityType,
                entityId: payload.entityId,
            });
        };

        const handleReconnected = (): void => {
            // SignalR does not replay missed messages and group membership is lost with the old
            // socket. Drop the stale roster and re-join everything we still hold.
            presenceStore.clear();
            presenceParticipation.rejoinAll();
        };

        connection.on(PRESENCE_EVENTS.memberActive, handleActive);
        connection.on(PRESENCE_EVENTS.memberLeft, handleLeft);
        connection.on(PRESENCE_EVENTS.syncRequested, handleSyncRequested);
        connection.onreconnected(handleReconnected);

        const heartbeat = window.setInterval(
            () => presenceParticipation.announceAll(),
            PRESENCE_HEARTBEAT_MS,
        );
        const prune = window.setInterval(
            () => presenceStore.pruneExpired(Date.now(), PRESENCE_TTL_MS),
            PRESENCE_PRUNE_INTERVAL_MS,
        );

        return () => {
            connection.off(PRESENCE_EVENTS.memberActive, handleActive);
            connection.off(PRESENCE_EVENTS.memberLeft, handleLeft);
            connection.off(PRESENCE_EVENTS.syncRequested, handleSyncRequested);
            window.clearInterval(heartbeat);
            window.clearInterval(prune);
            presenceStore.clear();
        };
    }, [connection]);

    // Re-point the participation manager whenever the connection or its state changes. When the
    // state transitions to connected, syncConnection (re)joins every held reference. The state
    // is observed through the store so a tick does not re-render this bridge.
    useEffect(() => {
        if (!PRESENCE_ENABLED) {
            return;
        }
        presenceParticipation.syncConnection(connection);
        return stateStore.subscribe(() => {
            presenceParticipation.syncConnection(connection);
        });
    }, [connection, stateStore]);
}
